import { useCallback, useEffect, useState } from 'react';

export type QueuePriority = 'high' | 'normal' | 'low';

export interface QueuedOperation<T = any> {
  id: string;
  priority: QueuePriority;
  operation: () => Promise<T>;
  retries?: number;
  maxRetries?: number;
  onSuccess?: (result: T) => void;
  onError?: (error: any) => void;
}

interface UseQueueOptions {
  maxRetries?: number;
  retryDelay?: (attempt: number) => number;
  onError?: (error: any, operation: QueuedOperation) => void;
}

export function useQueue(options?: UseQueueOptions) {
  const [queue, setQueue] = useState<QueuedOperation[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentOperation, setCurrentOperation] = useState<QueuedOperation | null>(null);

  const defaultOptions = {
    maxRetries: 3,
    retryDelay: (attempt: number) => Math.min(1000 * Math.pow(2, attempt), 10000),
    ...options,
  };

  const enqueue = useCallback(
    <T = any>(
      operation: () => Promise<T>,
      priority: QueuePriority = 'normal',
      callbacks?: {
        onSuccess?: (result: T) => void;
        onError?: (error: any) => void;
        maxRetries?: number;
      }
    ): string => {
      const id = `${Date.now()}-${Math.random()}`;
      const queuedOperation: QueuedOperation<T> = {
        id,
        priority,
        operation,
        retries: 0,
        maxRetries: callbacks?.maxRetries ?? defaultOptions.maxRetries,
        onSuccess: callbacks?.onSuccess,
        onError: callbacks?.onError,
      };

      setQueue((prev) => [...prev, queuedOperation]);
      return id;
    },
    [defaultOptions.maxRetries]
  );

  const dequeue = useCallback((id: string) => {
    setQueue((prev) => prev.filter((op) => op.id !== id));
  }, []);

  const clear = useCallback(() => {
    setQueue([]);
  }, []);

  const processQueue = useCallback(async () => {
    if (isProcessing || queue.length === 0) return;

    setIsProcessing(true);

    const sortedQueue = [...queue].sort((a, b) => {
      const priorityOrder = { high: 0, normal: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    const operation = sortedQueue[0];
    setCurrentOperation(operation);

    try {
      const result = await operation.operation();

      setQueue((prev) => prev.filter((op) => op.id !== operation.id));

      if (operation.onSuccess) {
        operation.onSuccess(result);
      }
    } catch (error) {
      console.error('Queue operation error:', error);

      const currentRetries = operation.retries || 0;
      const maxRetries = operation.maxRetries || defaultOptions.maxRetries!;

      if (currentRetries < maxRetries) {
        const delay = defaultOptions.retryDelay!(currentRetries);

        setTimeout(() => {
          setQueue((prev) => prev.map((op) => (op.id === operation.id ? { ...op, retries: currentRetries + 1 } : op)));
        }, delay);
      } else {
        setQueue((prev) => prev.filter((op) => op.id !== operation.id));

        if (operation.onError) {
          operation.onError(error);
        }
        if (defaultOptions.onError) {
          defaultOptions.onError(error, operation);
        }
      }
    } finally {
      setCurrentOperation(null);
      setIsProcessing(false);
    }
  }, [queue, isProcessing, defaultOptions]);

  useEffect(() => {
    processQueue();
  }, [processQueue]);

  return {
    enqueue,
    dequeue,
    clear,
    queue,
    isProcessing,
    currentOperation,
    queueSize: queue.length,
  };
}

export function useBatchQueue<T>(batchProcessor: (items: T[]) => Promise<void>, delay: number = 100) {
  const [pendingItems, setPendingItems] = useState<T[]>([]);
  const [batchTimeout, setBatchTimeout] = useState<NodeJS.Timeout | null>(null);
  const { enqueue } = useQueue();

  const add = useCallback(
    (item: T) => {
      setPendingItems((prev) => [...prev, item]);

      if (batchTimeout) {
        clearTimeout(batchTimeout);
      }

      const timeout = setTimeout(() => {
        const itemsToProcess = [...pendingItems, item];
        setPendingItems([]);

        enqueue(async () => batchProcessor(itemsToProcess), 'normal', {
          onError: (error) => console.error('Batch processing error:', error),
        });
      }, delay);

      setBatchTimeout(timeout);
    },
    [pendingItems, batchTimeout, delay, batchProcessor, enqueue]
  );

  const flush = useCallback(() => {
    if (batchTimeout) {
      clearTimeout(batchTimeout);
      setBatchTimeout(null);
    }

    if (pendingItems.length > 0) {
      const itemsToProcess = [...pendingItems];
      setPendingItems([]);
      enqueue(async () => batchProcessor(itemsToProcess), 'high');
    }
  }, [pendingItems, batchTimeout, batchProcessor, enqueue]);

  return {
    add,
    flush,
    pendingCount: pendingItems.length,
  };
}
